import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useSelector } from "react-redux";

import SIDE_BAR_SECTIONS, { SideBarItem } from "./sideBarItems";
import { DRAWER_WIDTH, MINI_DRAWER_WIDTH } from "utils/constants";
import { ReducersModal } from "utils/modals";
import { NavItems, USER_ROLE } from "utils/userType";
import Logo from "assets/images/logo.png";
import ROUTE_NAMES from "routes/routeNames";
import { BRAND } from "themes";

type SideBarProps = {
  open: boolean;
  onToggle: () => void;
};

const drawerTransition = (theme: { transitions: { create: Function; easing: { sharp: string }; duration: { enteringScreen: number } } }) =>
  theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  });

const paperSx = (width: number | string) => ({
  width,
  height: "100vh",
  maxHeight: "100vh",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  borderRight: "1px solid",
  borderColor: BRAND.border,
  background: "linear-gradient(180deg, #FBFAFF 0%, #F4F2FB 100%)",
  boxShadow: "8px 0 28px rgba(27, 19, 64, 0.12)",
  boxSizing: "border-box" as const,
  color: BRAND.ink,
});

function BrandMark() {
  return (
    <Box
      aria-hidden
      sx={{
        width: 40,
        height: 40,
        borderRadius: "12px",
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
        background: `linear-gradient(145deg, ${BRAND.primary} 0%, ${BRAND.secondary} 100%)`,
        boxShadow: "0 10px 18px -12px rgba(108, 60, 233, 0.9)",
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 24 24"
        sx={{ width: 22, height: 22, display: "block" }}
      >
        <circle cx="12" cy="12" r="8.2" fill="none" stroke="#fff" strokeWidth="1.7" />
        <circle cx="12" cy="12" r="2.2" fill="#fff" />
        <path
          d="M12 3.8v3M12 17.2v3M3.8 12h3M17.2 12h3M6.3 6.3l2.1 2.1M15.6 15.6l2.1 2.1M17.7 6.3l-2.1 2.1M8.4 15.6l-2.1 2.1"
          fill="none"
          stroke="#fff"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </Box>
    </Box>
  );
}

const SideBar = ({ open, onToggle }: SideBarProps) => {
  const { role } = useSelector((state: ReducersModal) => state.AuthReducer);
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const drawerWidth = isMobile ? DRAWER_WIDTH : open ? DRAWER_WIDTH : MINI_DRAWER_WIDTH;
  const showLabels = isMobile || open;

  const isItemVisible = (id: string) =>
    role === USER_ROLE.SUPER_ADMIN || !NavItems.includes(id);

  const isItemSelected = (url: string) => location.pathname.indexOf(url) > -1;

  const goToDashboard = () => {
    navigate(ROUTE_NAMES.MAIN_ROUTES.DASHBOARD);
    if (isMobile && open) onToggle();
  };

  const renderItem = (item: SideBarItem) => {
    const isSelected = isItemSelected(item.url);
    const Icon = item.Icon;

    const button = (
      <ListItemButton
        component={Link}
        to={item.url}
        selected={isSelected}
        aria-current={isSelected ? "page" : undefined}
        onClick={isMobile && open ? onToggle : undefined}
        sx={{
          minHeight: 40,
          width: showLabels ? "100%" : 44,
          mx: showLabels ? 0 : "auto",
          px: showLabels ? 1.25 : 0,
          py: 0.75,
          gap: 1.25,
          borderRadius: "12px",
          justifyContent: showLabels ? "flex-start" : "center",
          color: isSelected ? "#fff" : BRAND.ink,
          background: isSelected
            ? `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primaryDark} 100%)`
            : "transparent",
          boxShadow: isSelected ? "0 10px 18px -12px rgba(91, 47, 209, 0.9)" : "none",
          cursor: "pointer",
          transition: "background-color 200ms ease, color 200ms ease, box-shadow 200ms ease",
          "&:hover": {
            background: isSelected
              ? `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primaryDark} 100%)`
              : "rgba(108, 60, 233, 0.08)",
          },
          "&.Mui-selected": {
            background: `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primaryDark} 100%)`,
            color: "#fff",
            "&:hover": {
              background: `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primaryDark} 100%)`,
            },
          },
          "&.Mui-focusVisible": {
            outline: `2px solid ${BRAND.ink}`,
            outlineOffset: 2,
          },
          "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
          },
        }}
      >
        <Icon
          sx={{
            fontSize: 20,
            flexShrink: 0,
            color: isSelected ? "#fff" : BRAND.primary,
          }}
        />
        {showLabels && (
          <Typography
            component="span"
            sx={{
              fontWeight: isSelected ? 600 : 500,
              fontSize: "0.875rem",
              lineHeight: 1.25,
              letterSpacing: "-0.01em",
              color: "inherit",
              whiteSpace: "nowrap",
            }}
          >
            {item.title}
          </Typography>
        )}
      </ListItemButton>
    );

    return (
      <Box key={item.id} component="li" sx={{ listStyle: "none" }}>
        {showLabels ? (
          button
        ) : (
          <Tooltip title={item.title} placement="right" arrow>
            {button}
          </Tooltip>
        )}
      </Box>
    );
  };

  const collapseButton = (
    <IconButton
      size="small"
      onClick={onToggle}
      aria-label={showLabels ? "Collapse sidebar" : "Expand sidebar"}
      sx={{
        width: 32,
        height: 32,
        borderRadius: "10px",
        border: `1px solid ${BRAND.border}`,
        bgcolor: "#fff",
        color: BRAND.ink,
        cursor: "pointer",
        transition: "background-color 200ms ease, border-color 200ms ease",
        "&:hover": {
          bgcolor: BRAND.track,
          borderColor: "#DDD8EE",
        },
        "&.Mui-focusVisible": {
          outline: `2px solid ${BRAND.primary}`,
          outlineOffset: 2,
        },
      }}
    >
      {showLabels ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
    </IconButton>
  );

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, overflow: "hidden" }}>
      <Box
        sx={{
          px: showLabels ? 2 : 1,
          pt: 2,
          pb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: showLabels ? "space-between" : "center",
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Box
          onClick={goToDashboard}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              goToDashboard();
            }
          }}
          role="link"
          tabIndex={0}
          aria-label="Shinology, go to dashboard"
          sx={{
            display: "flex",
            alignItems: "center",
            minWidth: 0,
            cursor: "pointer",
            borderRadius: "12px",
            "&:focus-visible": {
              outline: `2px solid ${BRAND.primary}`,
              outlineOffset: 4,
            },
          }}
        >
          {showLabels ? (
            <Box sx={{ minWidth: 0 }}>
              <Box
                component="img"
                src={Logo}
                alt=""
                sx={{
                  height: 34,
                  width: "auto",
                  maxWidth: 168,
                  display: "block",
                  objectFit: "contain",
                }}
              />
              <Typography
                sx={{
                  mt: 0.75,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "#5C5870",
                  lineHeight: 1,
                }}
              >
                Admin console
              </Typography>
            </Box>
          ) : (
            <BrandMark />
          )}
        </Box>
        {showLabels && !isMobile && collapseButton}
      </Box>

      <Box
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          overflowX: "hidden",
          overflowY: "auto",
          overscrollBehavior: "contain",
          px: showLabels ? 1.75 : 1.25,
          pt: 0.5,
          pb: 1.5,
        }}
      >
          {SIDE_BAR_SECTIONS.map((section) => {
            const items = section.items.filter((item) => isItemVisible(item.id));
            if (!items.length) return null;

            return (
              <Box key={section.id} component="section" sx={{ mb: 0.5, "&:last-of-type": { mb: 0 } }}>
                {showLabels && (
                  <Typography
                    component="p"
                    sx={{
                      m: 0,
                      px: 1.25,
                      pt: 1,
                      pb: 0.5,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      color: "#5C5870",
                      lineHeight: 1,
                    }}
                  >
                    {section.label}
                  </Typography>
                )}
                <List
                  component="ul"
                  aria-label={section.label}
                  sx={{
                    py: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  {items.map(renderItem)}
                </List>
              </Box>
            );
          })}
      </Box>

      {!showLabels && (
        <Box
          sx={{
            py: 1.5,
            display: "flex",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {collapseButton}
        </Box>
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        open={open}
        anchor="left"
        variant="temporary"
        onClose={onToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          "& .MuiDrawer-paper": {
            ...paperSx(DRAWER_WIDTH),
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: 0,
        width: drawerWidth,
        transition: drawerTransition(theme),
        "@media (prefers-reduced-motion: reduce)": {
          transition: "none",
        },
      }}
      aria-label="sidebar"
    >
      <Drawer
        open
        anchor="left"
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            ...paperSx(drawerWidth),
            top: 0,
            transition: drawerTransition(theme),
          },
        }}
        ModalProps={{ keepMounted: true }}
        color="inherit"
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
};

export default SideBar;
